import { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import Modal from './Modal';

/**
 * ImageLightbox - Full-screen image gallery modal
 *
 * @param {Object} props
 * @param {string[]} props.images - Array of image URLs
 * @param {number} props.currentIndex - Currently displayed image index
 * @param {Function} props.onIndexChange - Called when navigation changes index
 * @param {Function} props.onClose - Called when lightbox should close
 */
export default function ImageLightbox({
  images = [],
  currentIndex = 0,
  onIndexChange,
  onClose,
}) {
  const hasMultiple = images.length > 1;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < images.length - 1;

  const goToPrev = useCallback(() => {
    if (canGoPrev) {
      onIndexChange?.(currentIndex - 1);
    }
  }, [canGoPrev, currentIndex, onIndexChange]);

  const goToNext = useCallback(() => {
    if (canGoNext) {
      onIndexChange?.(currentIndex + 1);
    }
  }, [canGoNext, currentIndex, onIndexChange]);

  // Arrow key navigation (Escape is handled by Modal)
  useEffect(() => {
    if (images.length === 0) return;
    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrev();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [images.length, goToPrev, goToNext]);

  return (
    <Modal
      isOpen={images.length > 0}
      onClose={onClose}
      onOverlayClick={onClose}
      overlayClassName="z-lightbox bg-black/90 animate-in fade-in duration-200"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
        title="Close (Esc)"
      >
        <X size={24} />
      </button>

      {/* Navigation arrows */}
      {hasMultiple && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrev();
            }}
            disabled={!canGoPrev}
            className={cn(
              'absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white transition-all z-10',
              canGoPrev ? 'hover:bg-white/20 cursor-pointer' : 'opacity-30 cursor-not-allowed'
            )}
            title="Previous (←)"
          >
            <ChevronLeft size={28} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            disabled={!canGoNext}
            className={cn(
              'absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white transition-all z-10',
              canGoNext ? 'hover:bg-white/20 cursor-pointer' : 'opacity-30 cursor-not-allowed'
            )}
            title="Next (→)"
          >
            <ChevronRight size={28} />
          </button>
        </>
      )}

      {/* Main image */}
      <div
        className="max-w-[90vw] max-h-[80dvh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1} of ${images.length}`}
          className="max-w-full max-h-[80dvh] object-contain rounded-lg shadow-2xl animate-in zoom-in-95 duration-200"
        />
      </div>

      {/* Image counter */}
      {hasMultiple && (
        <div className="absolute bottom-[calc(6rem+var(--sab))] left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-white/10 text-white text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Thumbnail strip */}
      {hasMultiple && (
        <div
          className="absolute bottom-[calc(1.5rem+var(--sab))] left-1/2 -translate-x-1/2 flex items-center gap-2 p-2 rounded-xl bg-black/50 backdrop-blur-sm"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => onIndexChange?.(index)}
              className={cn(
                'w-12 h-12 rounded-md overflow-hidden border-2 transition-all',
                index === currentIndex
                  ? 'border-white scale-110 shadow-lg'
                  : 'border-transparent opacity-60 hover:opacity-100'
              )}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}
